import ConfigurationSummaryList from '@/components/data/configuration/summary/ConfigurationSummaryList';
import DiscrepancyNoteGrid from '@/components/data/discrepancyNote/grid/DiscrepancyNoteGrid';
import PatientDataList from '@/components/data/patient/data/PatientDataList';
import Breadcrumbs from '@/components/layouts/Breadcrumbs';
import TrialContext from '@/contexts/data/TrialContext';
import useConfigurationSummaries from '@/hooks/data/useConfigurationSummaries';
import useCoordinatingGroups from '@/hooks/data/useCoordinatingGroups';
import useCountries from '@/hooks/data/useCountries';
import useInstitutions from '@/hooks/data/useInstitutions';
import useMasterGroups from '@/hooks/data/useMasterGroups';
import useMetadata from '@/hooks/data/useMetadata';
import usePatient from '@/hooks/data/usePatient';
import usePatientDataSummaries from '@/hooks/data/usePatientDataSummaries';
import useSourceDataNotesByTrialCode from '@/hooks/data/useSourceDataNotesByTrialCode';
import { faBoxArchive, faClipboardListCheck, faCogs, faDna, faFlag, faGlobeOceania, faPlus, faUser, faUsers } from '@fortawesome/duotone-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Grid, IconButton, Paper, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { FC, useContext, useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageTitle, { TITLE_SEPARATOR } from '@/components/layouts/PageTitle';


const Title = styled(Typography)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between'
}));

const Notes: FC = () => {


    const breadcrumbs = useMemo(() => {
        return [
            {
                label: 'Trials',
                link: `/`,
                icon: faDna
            },
            {
                label: 'Discrepancy Notes',
                link: `/`,
                icon: faFlag
            }
        ];
    }, []);

    const { trialCode } = useParams();
    const theme = useTheme();
    const { data: trial } = useContext(TrialContext);
    const { data: metadata, loading: metadataLoading, validating: metadataUpdating } = useMetadata(trialCode!, { revalidateOnFocus: false });
    const { data: notes, loading: notesLoading, validating: notesUpdating } = useSourceDataNotesByTrialCode(trialCode!, { revalidateOnFocus: false });
    const { data: institutions, loading: institutionsLoading, validating: institutionsUpdating  } = useInstitutions(trialCode!, { revalidateOnFocus: false });
    const { data: countries, loading: countriesLoading, validating: countriesUpdating  } = useCountries(trialCode!, { revalidateOnFocus: false });
    const { data: coordinatingGroups, loading: coordinatingGroupsLoading, validating: coordinatingGroupsUpdating  } = useCoordinatingGroups(trialCode!, { revalidateOnFocus: false });
    const { data: masterGroups, loading: masterGroupsLoading, validating: masterGroupsUpdating  } = useMasterGroups(trialCode!, { revalidateOnFocus: false });

    const loading = notesLoading || metadataLoading || institutionsLoading || countriesLoading || coordinatingGroupsLoading || masterGroupsLoading;
    const updating = !loading && (notesUpdating || metadataUpdating || institutionsUpdating || countriesUpdating || coordinatingGroupsUpdating || masterGroupsUpdating);
    
    const [ settingsOpen, setSettingsOpen ] = useState(false);

    return (
        <>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <PageTitle title={`Discrepancy Notes${TITLE_SEPARATOR}${trial?.name}`} />
            <Title 
                variant="h1" 
                gutterBottom
                color={theme.palette.secondary.main}
            >
                Discrepancy Notes
                <Box>
                    <IconButton onClick={() =>  setSettingsOpen(true)}>
                        <FontAwesomeIcon icon={faCogs} />
                    </IconButton>
                </Box>
            </Title>
            <DiscrepancyNoteGrid 
                masterGroups={masterGroups}
                coordinatingGroups={coordinatingGroups}
                countries={countries}
                institutions={institutions}
                metadata={metadata}
                notes={notes}
                loading={loading}
                updating={updating}
                settingsOpen={settingsOpen}
                onSettingsClose={() => setSettingsOpen(false)}
            />
        </>
    );
}

export default Notes;